import { api, apiErrorMessage } from '@ddone/api-client';
import { Button, ErrorState, Modal, Skeleton } from '@ddone/ui';
import { useQuery } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';

interface ActivityItem {
  id: string;
  actorName: string;
  actionLabel: string;
  entityLabel?: string;
  moduleId: string;
  occurredAt: string;
}

export function ActivityDrawer({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
}) {
  const query = useQuery({
    queryKey: ['activity', 'recent'],
    queryFn: () => api.request<ActivityItem[]>('/activity?limit=30'),
    enabled: open
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Registro modifiche"
      description="Ultime operazioni tracciate nel workspace per responsabilità e controllo."
      footer={
        <Button variant="secondary" onClick={() => void query.refetch()}>
          <RotateCw size={15} /> Aggiorna
        </Button>
      }
    >
      <div className="dd-activity-list">
        {query.isPending
          ? Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="dd-activity-skeleton" />
            ))
          : null}
        {query.isError ? (
          <ErrorState message={apiErrorMessage(query.error)} onRetry={() => void query.refetch()} />
        ) : null}
        {query.isSuccess && query.data.length === 0 ? (
          <p className="dd-activity-empty">
            Non ci sono ancora modifiche registrate in questo workspace.
          </p>
        ) : null}
        {query.data?.map((item) => (
          <article key={item.id}>
            <span className="dd-activity-dot" />
            <div>
              <p>
                <strong>{item.actorName}</strong> {item.actionLabel}{' '}
                {item.entityLabel ? <b>{item.entityLabel}</b> : null}
              </p>
              <small>
                {moduleLabel(item.moduleId)} ·{' '}
                {new Intl.DateTimeFormat('it-IT', {
                  dateStyle: 'medium',
                  timeStyle: 'short'
                }).format(new Date(item.occurredAt))}
              </small>
            </div>
          </article>
        ))}
      </div>
    </Modal>
  );
}

const moduleLabels: Record<string, string> = {
  core: 'Sistema',
  clients: 'Clienti',
  crm: 'CRM',
  projects: 'Progetti',
  notes: 'Note',
  tasks: 'Attività',
  'time-tracking': 'Tempo',
  billing: 'Scadenze',
  expenses: 'Spese',
  settings: 'Impostazioni'
};

function moduleLabel(moduleId: string): string {
  return moduleLabels[moduleId] ?? moduleId;
}
